import { Box, ButtonBase, CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined';
import clsx from 'clsx';
import PgIcon from 'Components/PgIcon';
import React, { FC } from 'react';
import { COLOR } from 'Theme/themeConstants';

const MAX_FILENAME_LENGTH = 12;

export interface FileUploadPreviewProps {
    fileName: string;
    isLoading: boolean;
    onRemove: () => void;
    className?: string;
}

const truncateFileName = (name: string): string => {
    if (name.length <= MAX_FILENAME_LENGTH) return name;
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
    const base = name.slice(0, name.length - ext.length);
    const maxBase = MAX_FILENAME_LENGTH - ext.length;
    return base.length <= maxBase ? name : `${base.slice(0, Math.max(0, maxBase - 3))}...${ext}`;
};

const FileUploadPreview: FC<FileUploadPreviewProps> = ({ fileName, isLoading, onRemove, className }) => {
    const classes = useStyles();

    return (
        <Box
            component={ButtonBase}
            className={clsx(classes.wrap, className)}
            onClick={isLoading ? undefined : onRemove}
            style={{ cursor: isLoading ? 'default' : undefined }}
        >
            <InsertDriveFileOutlined className={classes.fileIcon} />
            <Box component="span" className={classes.fileName}>
                {truncateFileName(fileName)}
            </Box>
            <Box className={classes.rightSlot}>
                {isLoading ? (
                    <CircularProgress size={14} />
                ) : (
                    <PgIcon icon="icon-close" styleClass={classes.closeIcon} />
                )}
            </Box>
        </Box>
    );
};

const useStyles = makeStyles<Theme>((theme) =>
    createStyles({
        wrap: {
            minWidth: theme.spacing(4),
            height: theme.spacing(4),
            maxWidth: 140,
            padding: theme.spacing(0, 0.75),
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.75),
            borderRadius: 4,
            backgroundColor: COLOR.tertiary.music,
        },
        fileIcon: {
            fontSize: theme.spacing(2),
            color: theme.palette.grey[700],
            flexShrink: 0,
        },
        fileName: {
            fontSize: 10,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            color: theme.palette.text.primary,
        },
        rightSlot: {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
        },
        closeIcon: {
            fontSize: theme.spacing(1),
            WebkitTextStrokeWidth: '1px',
        },
    }),
);

export default FileUploadPreview;
